import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Check, X, Settings2, Globe, Clock, Calendar, Bell, MessageCircle, CheckSquare, Briefcase, AlertTriangle, Zap, Megaphone, Monitor, ClipboardList } from "lucide-react";
import { usePreferences } from "../contexts/PreferencesContext";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "./ui/sonner";

const TIMEZONES = [
  { value: "America/Sao_Paulo",    label: "América/São Paulo (GMT-3)" },
  { value: "America/Manaus",       label: "América/Manaus (GMT-4)" },
  { value: "America/Fortaleza",    label: "América/Fortaleza (GMT-3)" },
  { value: "America/Belem",        label: "América/Belém (GMT-3)" },
  { value: "America/Noronha",      label: "América/Noronha (GMT-2)" },
  { value: "America/New_York",     label: "América/New York (GMT-5/-4)" },
  { value: "America/Chicago",      label: "América/Chicago (GMT-6/-5)" },
  { value: "America/Los_Angeles",  label: "América/Los Angeles (GMT-8/-7)" },
  { value: "America/Mexico_City",  label: "América/Cidade do México (GMT-6)" },
  { value: "America/Buenos_Aires", label: "América/Buenos Aires (GMT-3)" },
  { value: "America/Santiago",     label: "América/Santiago (GMT-4/-3)" },
  { value: "America/Lima",         label: "América/Lima (GMT-5)" },
  { value: "America/Bogota",       label: "América/Bogotá (GMT-5)" },
  { value: "Europe/Lisbon",        label: "Europa/Lisboa (GMT+0/+1)" },
  { value: "Europe/London",        label: "Europa/Londres (GMT+0/+1)" },
  { value: "Europe/Paris",         label: "Europa/Paris (GMT+1/+2)" },
  { value: "Europe/Berlin",        label: "Europa/Berlim (GMT+1/+2)" },
  { value: "UTC",                  label: "UTC (GMT+0)" },
];

const DATE_FORMATS = [
  { value: "dd/MM/yyyy",  label: "31/12/2026 (dd/MM/yyyy)" },
  { value: "MM/dd/yyyy",  label: "12/31/2026 (MM/dd/yyyy)" },
  { value: "yyyy-MM-dd",  label: "2026-12-31 (yyyy-MM-dd)" },
  { value: "dd MMM yyyy", label: "31 Dez 2026 (dd MMM yyyy)" },
];

const NOTIF_OPTIONS = [
  { key: "whatsapp",          icon: MessageCircle, color: "#10b981", label: "Mensagens WhatsApp",       desc: "Quando recebo uma nova mensagem de um contato" },
  { key: "form_lead",         icon: ClipboardList, color: "#6366f1", label: "Lead via Formulário",      desc: "Quando alguém preenche um formulário público e um lead é criado" },
  { key: "tarefa_atribuida",  icon: CheckSquare,   color: "#3b82f6", label: "Tarefa atribuída a mim",  desc: "Quando alguém me atribui uma nova tarefa" },
  { key: "tarefa_vencendo",   icon: Clock,         color: "#f59e0b", label: "Tarefa vencendo hoje",    desc: "Aviso diário sobre tarefas com vencimento hoje" },
  { key: "negocio_atribuido", icon: Briefcase,     color: "#6366f1", label: "Negócio atribuído a mim", desc: "Quando sou definido como responsável de um negócio" },
  { key: "sla_breach",        icon: AlertTriangle, color: "#ef4444", label: "SLA excedido",            desc: "Quando um negócio ultrapassa o prazo da etapa" },
  { key: "negocio_finalizado",icon: Check,         color: "#8b5cf6", label: "Negócio ganho/perdido",   desc: "Quando um negócio que acompanho é finalizado" },
  { key: "sistema",           icon: Zap,           color: "#94a3b8", label: "Notificações do sistema", desc: "Automações executadas e avisos do sistema" },
  { key: "broadcast",         icon: Megaphone,     color: "#f97316", label: "Mensagens da equipe",     desc: "Comunicados do administrador para todos", locked: true },
];

const BROWSER_OPTIONS = [
  { key: "browser_whatsapp", icon: MessageCircle, color: "#10b981", label: "Push: mensagens WhatsApp", desc: "Alerta mesmo com o sistema minimizado" },
  { key: "browser_sla",      icon: AlertTriangle, color: "#ef4444", label: "Push: SLA excedido",      desc: "Alerta crítico de prazo ultrapassado" },
];

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative rounded-full transition-colors flex-shrink-0 overflow-hidden ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      style={{ backgroundColor: checked ? "#10b981" : "#d1d5db", height: 24, width: 44, minWidth: 44 }}
    >
      <span
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-200"
        style={{ left: checked ? 24 : 4 }}
      />
    </button>
  );
}

export default function PreferenciasModal({ open, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const { prefs, updatePrefs } = usePreferences();
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";
  const [activeTab, setActiveTab] = useState("idiomas");
  const [form, setForm] = useState({ ...prefs });

  useEffect(() => {
    if (open) { setForm({ ...prefs }); setActiveTab("idiomas"); }
  }, [open, prefs]);

  const getNotifPref = (key) => {
    const defaults = {
      whatsapp: true, tarefa_atribuida: true, tarefa_vencendo: true,
      negocio_atribuido: true, sla_breach: true, negocio_finalizado: true,
      sistema: true, broadcast: true, browser_whatsapp: false, browser_sla: false,
    };
    return { ...defaults, ...(form.notification_prefs || {}) };
  };

  const setNotifPref = (key, val) => {
    setForm(prev => ({
      ...prev,
      notification_prefs: { ...getNotifPref(), [key]: val }
    }));
  };

  const requestBrowserPermission = async (key) => {
    if (!("Notification" in window)) { toast.error("Seu navegador não suporta notificações push."); return; }
    const perm = await Notification.requestPermission();
    if (perm === "granted") setNotifPref(key, true);
    else toast.error("Permissão de notificação negada pelo navegador.");
  };

  const handleSave = async () => {
    await updatePrefs(form);
    toast.success(t("preferencias.saved"));
    onOpenChange(false);
  };

  const languages = [
    { value: "pt-BR", label: "🇧🇷  Português (Brasil)" },
    { value: "en-US", label: "🇺🇸  English (US)" },
    { value: "es-ES", label: "🇪🇸  Español (España)" },
  ];

  const notifPrefs = getNotifPref();

  const TABS = [
    { key: "idiomas",       label: "Idiomas",       icon: Globe },
    { key: "notificacoes",  label: "Notificações",  icon: Bell },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg !p-0 overflow-hidden sm:!p-0">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
              <Settings2 className="w-4 h-4" style={{ color: tc500 }} />
            </div>
            {t("preferencias.title")}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-slate-200 px-5 mt-3">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${isActive ? "border-b-2 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                style={isActive ? { borderBottomColor: tc500, color: tc500 } : undefined}>
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">

          {/* ── TAB: IDIOMAS ── */}
          {activeTab === "idiomas" && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5 text-slate-400" />{t("preferencias.language")}
                </Label>
                <Select value={form.language || "pt-BR"} onValueChange={v => setForm({ ...form, language: v })}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {languages.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />{t("preferencias.timezone")}
                </Label>
                <Select value={form.timezone || "America/Sao_Paulo"} onValueChange={v => setForm({ ...form, timezone: v })}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value} className="text-sm">{tz.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />{t("preferencias.dateFormat")}
                </Label>
                <Select value={form.date_format || "dd/MM/yyyy"} onValueChange={v => setForm({ ...form, date_format: v })}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DATE_FORMATS.map(f => <SelectItem key={f.value} value={f.value} className="text-sm">{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* ── TAB: NOTIFICAÇÕES ── */}
          {activeTab === "notificacoes" && (
            <div className="space-y-5">
              {/* In-app notifications */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Bell className="w-3.5 h-3.5" />Notificações no sistema
                </p>
                <div className="space-y-2">
                  {NOTIF_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <div key={opt.key} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${opt.color}15` }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: opt.color }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 leading-tight">{opt.label}</p>
                            <p className="text-xs text-slate-400 leading-tight mt-0.5">{opt.desc}</p>
                          </div>
                        </div>
                        <Toggle
                          checked={notifPrefs[opt.key]}
                          onChange={val => setNotifPref(opt.key, val)}
                          disabled={opt.locked}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Browser push notifications */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Monitor className="w-3.5 h-3.5" />Notificações do navegador (push)
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-xs text-amber-700">
                  Exige permissão do navegador. Funciona mesmo com o sistema em outra aba.
                </div>
                <div className="space-y-2">
                  {BROWSER_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const isGranted = typeof window !== "undefined" && Notification?.permission === "granted";
                    return (
                      <div key={opt.key} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${opt.color}15` }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: opt.color }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 leading-tight">{opt.label}</p>
                            <p className="text-xs text-slate-400 leading-tight mt-0.5">{opt.desc}</p>
                          </div>
                        </div>
                        <Toggle
                          checked={notifPrefs[opt.key] && isGranted}
                          onChange={val => val ? requestBrowserPermission(opt.key) : setNotifPref(opt.key, false)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-slate-200">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-2" />{t("common.cancel")}
          </Button>
          <Button className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} onClick={handleSave}>
            <Check className="w-4 h-4 mr-2" />{t("common.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
